import { Component, Input, HostListener, ChangeDetectionStrategy } from '@angular/core';
import { Subscription } from 'rxjs';
import { MyConfigOrganization } from '../../../../models/myConfig';
import { BackOffice24StatesService } from '../state/back-office24-states.service';
import { BackOfficeService } from '../../../../services/backoffice.service';
import Hls, { type HlsConfig } from "hls.js";
import { MyConfigService } from '../../../../services/my-config.service';

@Component({
  selector: 'app-cameras',
  standalone: false,
  templateUrl: './cameras.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './cameras.component.css'
})
export class CamerasComponent {
  @Input() organization: MyConfigOrganization | null = null;
  @Input() showAllOrganizations = false;
  loading = false;
  private orgSubscription: Subscription = new Subscription();
  private camerasSubscription: Subscription = new Subscription();
  private readonly ALL_ORGS_CAMERA_ORG_ID = 'f812a7d8-e4a6-48e2-9ed0-827317772863';
  cameras: any;
  groupedCameras: { organizationName: string; cameras: any[] }[] = [];
  isAllOrgView = false;
  private hlsInstances: { [key: string]: Hls } = {};
  cameraStates: { [key: string]: boolean } = {};
  cctvColumns = 2;
  isCctvFullscreen = false;
  surCamerasList: string[] = [];

  getCameraDescription(sensorId: string): string {
    const cam = this.cameras?.find((c: any) => c.sensorId === sensorId);
    return cam?.description || sensorId;
  }

  private readonly overlayPalette = [
    { bg: '#9085FF', border: '#6e62e0' },
    { bg: '#1B3777', border: '#142a5a' },
    { bg: '#397863', border: '#2a5a49' },
  ];

  getOverlayTitleStyle(index: number): { [key: string]: string } {
    const color = this.overlayPalette[index % this.overlayPalette.length];
    return { backgroundColor: color.bg, borderLeftColor: color.border, color: '#ffffff' };
  }
  archiveCamera: string = '';
  archiveDate: string = new Date().toISOString().slice(0, 10);
  archiveHour: string = '00';
  archiveMinute: string = '00';
  archivePlaying = false;
  archivePlaybackRate = 1;
  readonly archiveSpeeds: number[] = [1, 4, 16];

  readonly hours: string[] = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'));
  readonly minutes: string[] = ['00', '10', '20', '30', '40', '50'];

  constructor(
    private backOffice24StatesService: BackOffice24StatesService,
    private backofficeService: BackOfficeService,
    private myConfigService: MyConfigService

  ) { }

  ngOnInit(): void {
    this.orgSubscription = this.backOffice24StatesService.currentOrganization$.subscribe(
      org => {
        this.organization = org;
        this.loadCameras();
      }
    );
  }

  private loadCameras(): void {
    if (this.camerasSubscription) {
      this.camerasSubscription.unsubscribe();
    }

    const orgId = this.organization?.organizationId;
    if (!orgId) {
      this.cameras = [];
      this.surCamerasList = [];
      return;
    }

    this.isAllOrgView = orgId === this.ALL_ORGS_CAMERA_ORG_ID;
    const cameras$ = this.isAllOrgView
      ? this.backofficeService.getAllCameras()
      : this.backofficeService.getCameras(orgId);

    this.camerasSubscription = cameras$.subscribe(
      cameras => {
        this.cameras = cameras;
        this.groupedCameras = this.isAllOrgView
          ? this.buildCameraGroups(cameras as any[])
          : [{ organizationName: this.organization?.organizationName || 'Current Organization', cameras: cameras as any[] }];

        this.surCamerasList = (cameras as any[])
          // .filter(c => c.sensorId?.includes('-sur-'))
          .map(c => c.sensorId);
        if (this.surCamerasList.length > 0) this.archiveCamera = this.surCamerasList[0];

        // Trigger layout calculation after the monitor tab is rendered
        setTimeout(() => this.updateLayoutDimensions(), 150);
      },
      error => { console.log(error); }
    );
  }

  ngOnDestroy(): void {
    this.orgSubscription.unsubscribe();
    this.camerasSubscription.unsubscribe();
    Object.keys(this.hlsInstances).forEach((key) => {
      this.hlsInstances[key]?.destroy();
    });
    this.hlsInstances = {};
    document.removeEventListener('fullscreenchange', this.onFullscreenChange);
  }

  private buildCameraGroups(cameras: any[]): { organizationName: string; cameras: any[] }[] {
    const groups: { [key: string]: { organizationName: string; cameras: any[] } } = {};

    const orgsById = new Map<string, string>();
    const config = this.myConfigService.getMyConfig();
    config?.organizations?.forEach((org: MyConfigOrganization) => {
      if (org.organizationId) {
        orgsById.set(org.organizationId.toLowerCase(), org.organizationName);
      }
    });

    cameras.forEach(cam => {
      const orgId = cam.organizationId || cam.organization?.organizationId;
      const orgName = orgId
        ? (orgsById.get(orgId.toLowerCase()) || cam.organizationName || cam.organization?.organizationName || 'Unknown Organization')
        : (cam.organizationName || cam.organization?.organizationName || 'Unknown Organization');

      if (!groups[orgName]) {
        groups[orgName] = { organizationName: orgName, cameras: [] };
      }
      groups[orgName].cameras.push(cam);
    });

    return Object.values(groups);
  }

  private onFullscreenChange = () => {
    this.isCctvFullscreen = !!document.fullscreenElement;
    // Re-compute layout after fullscreen transition
    setTimeout(() => this.updateLayoutDimensions(), 100);
  };

  get archiveMinDate(): string {
    return new Date(Date.now() - 72 * 60 * 60 * 1000).toISOString().slice(0, 10);
  }

  get archiveMaxDate(): string {
    return new Date(Date.now() - 5 * 60 * 1000).toISOString().slice(0, 10);
  }

  get archiveMaxHour(): string {
    const maxDt = new Date(Date.now() - 5 * 60 * 1000);
    const maxDate = maxDt.toISOString().slice(0, 10);
    if (this.archiveDate === maxDate) {
      return maxDt.getHours().toString().padStart(2, '0');
    }
    return '23';
  }

  getAvailableMinutes(): string[] {
    const maxDt = new Date(Date.now() - 5 * 60 * 1000);
    const maxDate = maxDt.toISOString().slice(0, 10);
    if (this.archiveDate === maxDate && this.archiveHour === this.archiveMaxHour) {
      const maxMin = Math.floor(maxDt.getMinutes() / 10) * 10;
      return this.minutes.filter(m => parseInt(m) <= maxMin);
    }
    return this.minutes;
  }

  onDateChange(): void {
    const maxHour = this.archiveMaxHour;
    if (this.archiveHour > maxHour) {
      this.archiveHour = maxHour;
      this.archiveMinute = '00';
    }
  }

  onHourChange(): void {
    const available = this.getAvailableMinutes();
    if (!available.includes(this.archiveMinute)) {
      this.archiveMinute = available[available.length - 1] ?? '00';
    }
  }

  loadArchiveStream(): void {
    if (!this.archiveCamera || !this.archiveDate) return;
    const startEpoch = Math.floor(new Date(`${this.archiveDate}T${this.archiveHour}:${this.archiveMinute}`).getTime() / 1000);
    const endEpoch = startEpoch + 600;
    const url = this.backofficeService.getArchivePlaylistUrl(this.archiveCamera, startEpoch, endEpoch);
    const video = document.getElementById('archive-player') as HTMLVideoElement;
    if (!video) return;
    this.loadHls('archive-player', video, url, { maxBufferLength: 60, maxMaxBufferLength: 120 });
  }

  setArchiveSpeed(speed: number): void {
    this.archivePlaybackRate = speed;
    const video = document.getElementById('archive-player') as HTMLVideoElement;
    if (video) video.playbackRate = speed;
  }

  startCameraStream(id: string, suffix: string = ''): void {
    const videoId = id + suffix;
    const video = document.getElementById(videoId) as HTMLVideoElement;
    if (!video) return;
    const streamUrl = this.backofficeService.getCameraStreamUrl(id);
    this.loadHls(videoId, video, streamUrl, { liveSyncDuration: 2, liveMaxLatencyDuration: 5, maxBufferLength: 4, maxMaxBufferLength: 8 });
  }

  private loadHls(id: string, video: HTMLVideoElement, url: string, config: Partial<HlsConfig>): void {
    const existing = this.hlsInstances[id];
    if (existing) { existing.destroy(); delete this.hlsInstances[id]; }
    video.pause();
    video.src = '';

    if (Hls.isSupported()) {
      const hls = new Hls(config);
      this.hlsInstances[id] = hls;
      hls.loadSource(url);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        video.play().then(() => {
          this.cameraStates[id] = true;
          if (id === 'archive-player') this.setArchiveSpeed(this.archivePlaybackRate);
        });
      });
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = url;
      video.addEventListener('loadedmetadata', () => {
        video.play().then(() => {
          this.cameraStates[id] = true;
          if (id === 'archive-player') this.setArchiveSpeed(this.archivePlaybackRate);
        });
      });
    }
  }

  stopStream(camId: string, suffix: string = ''): void {
    const videoId = camId + suffix;
    const video = document.getElementById(videoId) as HTMLVideoElement;
    if (!video) return;
    const hls = this.hlsInstances[videoId];
    if (hls) { hls.destroy(); delete this.hlsInstances[videoId]; }
    video.pause();
    video.src = '';
    this.cameraStates[videoId] = false;
  }

  startAllStreams(suffix: string = ''): void {
    if (this.cameras) {
      this.cameras.forEach((cam: any) => {
        this.startCameraStream(cam.sensorId, suffix);
      });
    }
  }

  stopAllStreams(suffix: string = ''): void {
    if (this.cameras) {
      this.cameras.forEach((cam: any) => {
        this.stopStream(cam.sensorId, suffix);
      });
    }
  }

  enlargeVideo(element: HTMLElement) {
    if (element.requestFullscreen) {
      element.requestFullscreen();
    } else if ((element as any).webkitRequestFullscreen) {
      (element as any).webkitRequestFullscreen();
    } else if ((element as any).msRequestFullscreen) {
      (element as any).msRequestFullscreen();
    }
    document.addEventListener('fullscreenchange', this.onFullscreenChange);
  }

  exitFullscreen(): void {
    if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if ((document as any).webkitExitFullscreen) {
      (document as any).webkitExitFullscreen();
    }
  }

  @HostListener('window:resize')
  onResize() {
    this.updateLayoutDimensions();
  }

  onTabChange(event: any): void {
    if (event.tab.textLabel === 'Monitor') {
      setTimeout(() => this.updateLayoutDimensions(), 150);
    }
  }

  updateLayoutDimensions(): void {
    const container = document.querySelector('.cctv-container') as HTMLElement;
    if (!container || !this.cameras || this.cameras.length === 0) return;

    const header = container.querySelector('.cctv-header') as HTMLElement;
    const headerH = header ? header.offsetHeight + 12 : 70;
    const viewportTop = container.getBoundingClientRect().top;
    const availableHeight = Math.max(240, window.innerHeight - viewportTop - 24);
    const availableWidth = Math.max(280, container.clientWidth - 16);
    const gridHeight = Math.max(220, availableHeight - headerH);
    const N = this.cameras.length;

    if (availableWidth <= 0 || gridHeight <= 0) {
      setTimeout(() => this.updateLayoutDimensions(), 100);
      return;
    }

    const GAP = 8;
    let bestArea = 0;
    let bestHeight = 0;
    let bestCols = 1;

    for (let c = 1; c <= N; c++) {
      const r = Math.ceil(N / c);
      const maxW = (availableWidth - (c - 1) * GAP) / c;
      const maxH = (gridHeight - (r - 1) * GAP) / r;

      let w = maxW;
      let h = w * (9 / 16);

      if (h > maxH) {
        h = maxH;
        w = h * (16 / 9);
      }

      const area = w * h;
      if (area > bestArea) {
        bestArea = area;
        bestHeight = h;
        bestCols = c;
      }
    }

    this.cctvColumns = bestCols;

    const grid = document.querySelector('.cctv-grid') as HTMLElement;
    if (grid) {
      grid.style.setProperty('--cctv-cols', `${bestCols}`);
      grid.style.setProperty('--cctv-card-width', `${Math.floor(Math.max(220, Math.min(availableWidth / bestCols - GAP, (bestHeight * 16) / 9)))}px`);
      grid.style.setProperty('--cctv-card-height', `${Math.floor(bestHeight)}px`);
      grid.style.setProperty('--cctv-gap', `${GAP}px`);
    }
  }
}
